import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function StaffordshireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Staffordshire Pothole Claims | 143,689 Potholes in Five Years | Fixtyer"
        description="Staffordshire County Council filled a record 37,308 potholes in 2024/25 and admits a 181-year repair backlog at current funding. RED-condition C roads have doubled since 2020, and unclassified roads went up to six years between surveys. See what the DfT AMBER rating means for your pothole claim."
        canonical="/councils/staffordshire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Staffordshire', url: '/councils/staffordshire' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Staffordshire says it fills over 30,000 potholes a year — doesn\u2019t that prove the roads are well maintained?',
            answer: 'No — it proves the opposite. A network that needs 30,000-plus pothole repairs a year is a network producing defects faster than planned maintenance can prevent them. Repairs hit a record 37,308 in 2024/25 — over 100 every day — and the five-year total is 143,689. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on how many others the council patched.',
          },
          {
            question: 'What if my pothole was on an unclassified or residential road?',
            answer: 'Unclassified roads make up 3,488km — 59% of Staffordshire\u2019s road network — and before 2024 the council surveyed them, in its own words, "on a frequency up to every 6 years". When 100% annual surveys began in 2024, the proportion in RED condition jumped from 8% to 14% — roughly 488km of residential streets and rural lanes flagged for maintenance that the old survey cycle had not captured.',
          },
          {
            question: 'Staffordshire\u2019s DfT Spend rating is GREEN — can I still claim?',
            answer: 'Yes. The council spent £64.7 million of capital in 2024/25 against a £28.3 million DfT allocation, which is why Spend is GREEN. But Condition and Best Practice are both AMBER, and the preventative share of spending has fallen from 41% in 2021/22 to a projected 29% in 2025/26. Your claim is decided on the specific defect and the inspection record for that road, not the size of the council\u2019s chequebook.',
          },
          {
            question: 'Does the council\u2019s "181 years" admission help my claim?',
            answer: 'Yes. In October 2025 Staffordshire publicly stated that at current funding levels it would take 181 years to repair every road in the county, and 515 years to bring all bridges and structures up to standard. That is documented knowledge of a network-wide repair backlog — powerful context when testing whether the council\u2019s inspection and repair regime was "reasonable" under Section 58 for the road that damaged your vehicle.',
          },
          {
            question: 'My pothole was on a C road — does the condition data help me?',
            answer: 'Strongly. C roads are the worst-performing class in Staffordshire\u2019s own data: the proportion in RED condition has doubled from 6% in 2020 to 12% in 2024, and only 59% of C roads are now in good condition. Because the council surveys 100% of its B and C network every year, it cannot argue it was unaware of the deterioration — the trend is documented in its own transparency report.',
          },
          {
            question: 'A roads jumped to 86% green in 2024 — does that weaken a claim on a main road?',
            answer: 'Not necessarily. The leap from 71% to 86% green in a single year coincides with step changes across the 2024 survey data — the same year RED unclassified roads jumped from 8% to 14%, which the council attributes to a change in survey technique. A-road claims still turn on the specific defect: prior reports, photographs showing size and age, and the inspection log for that stretch of road.',
          },
        ]}
      />
      <ServiceSchema 
        councilName="Staffordshire County Council" 
        councilSlug="staffordshire" 
        rating="amber" 
      />
      <SpeakableSchema
        url="/councils/staffordshire"
        headline="Staffordshire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div 
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Staffordshire</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>
          
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Staffordshire: A 181-Year Repair Backlog, By Its Own Maths
          </h1>
          
          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Staffordshire County Council spends more than double its DfT capital allocation and earns a 
            GREEN scorecard for spend. Yet the overall rating is <strong className="text-amber-400">AMBER</strong> — 
            RED-condition C roads have <strong className="text-rose-400">doubled since 2020</strong>, unclassified 
            roads went up to six years between condition surveys, and the council itself says that at current 
            funding it would take <strong className="text-rose-400">181 years</strong> to repair every road 
            in the county.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">37,308</div>
            <div className="text-white font-medium mt-2">Potholes filled in 2024/25 — a five-year record</div>
            <div className="text-brand-300 text-sm mt-1">
              More than 100 potholes every single day, and over double the 15,502 filled in 2020/21, 
              across Staffordshire's 5,945km road network.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Staffordshire's own transparency report — main roads 
            reportedly improving, C roads and unclassified roads going the other way
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (735km — 12% of network): a sudden leap</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">2%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">from 3% in 2023</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">12%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">down from 26% in 2023</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">86%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">up from 71% in 2023</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              A roads went from 71% green to 86% green in a single year — a 15-point jump after four years 
              of hovering between 71% and 75%. The 2024 figures across all road classes show step changes 
              that coincide with the county's AI-driven video survey approach, so treat single-year 
              comparisons with caution. Either way, A roads are just one-eighth of the network.
            </p>
          </div>

          {/* B roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B roads: broadly stable</h3>
            <p className="text-surface-600 text-sm mb-4">
              B-road RED condition has sat at 3-4% across all five years (3% in 2020, 4% in 2023, 3% in 2024), 
              with green rising to 80% in 2024. B and C roads together make up 1,722km — 29% of the network — 
              and are surveyed in full every year.
            </p>
          </div>

          {/* C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">C roads: RED condition has doubled since 2020</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">62%</td>
                    <td className="px-4 py-2 text-center">32%</td>
                    <td className="px-4 py-2 text-center">6%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">66%</td>
                    <td className="px-4 py-2 text-center">30%</td>
                    <td className="px-4 py-2 text-center">5%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">62%</td>
                    <td className="px-4 py-2 text-center">31%</td>
                    <td className="px-4 py-2 text-center">7%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">60%</td>
                    <td className="px-4 py-2 text-center">31%</td>
                    <td className="px-4 py-2 text-center">9%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">59%</td>
                    <td className="px-4 py-2 text-center font-bold">30%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">12%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED-condition C roads have doubled from 6% to 12% since 2020 — and the deterioration is 
              accelerating, rising every single year since 2021. Two in five C roads now need maintenance 
              or will soon. Notably, this decline shows up even in the same 2024 survey that reported 
              dramatic improvements on A roads.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And This Is The Well-Funded Version</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£28.3m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2024/25</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£64.7m</div>
                <div className="text-brand-300 text-sm">Actual capital spend 2024/25</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-rose-400">29%</div>
                <div className="text-brand-300 text-sm">Preventative share 2025/26 — down from 41% in 2021/22</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Staffordshire spent more than <span className="text-emerald-400">double its DfT allocation</span> in 
              2024/25 — and C roads and unclassified roads are still declining. Meanwhile the preventative 
              share of spending has fallen for four consecutive years, from 41% to a projected 29%. 
              Less prevention now means more potholes later.
            </p>
          </div>
        </div>
      </section>

      {/* The Six-Year Blind Spot */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 3,488km Blind Spot
          </h2>
          <p className="text-surface-600 mb-8">
            59% of the network is unclassified roads — surveyed "up to every 6 years" until 2024. 
            When annual surveys finally arrived, RED condition nearly doubled overnight.
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U roads in RED condition</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Survey coverage</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center">10%</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Partial — up to 6-year cycle</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center">8%</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Partial — up to 6-year cycle</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center">9%</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Partial — up to 6-year cycle</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center">8%</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Partial — up to 6-year cycle</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14%</td>
                  <td className="px-6 py-4 text-center font-medium">100% annual — first time</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Council's Own Admission</h3>
              <p className="text-surface-600 text-sm mb-3">
                Staffordshire's transparency report states: "Surveys have been carried out across 100% of 
                the unclassified road network annually since 2024. Prior to this condition data was 
                collected using a different survey technique on a frequency up to every 6 years, which 
                explains the step change in results in 2024."
              </p>
              <p className="text-surface-600 text-sm">
                Read that carefully. For the road type that makes up <strong>59% of the county</strong> — 
                3,488km of residential streets, estate roads and rural lanes — a given road could go up 
                to <strong>six years</strong> between condition surveys before 2024.
              </p>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">What Full Coverage Revealed</h3>
              <p className="text-rose-800 text-sm mb-3">
                The moment Staffordshire surveyed 100% of its unclassified network in a single year, the 
                RED figure jumped from 8% to 14% — roughly <strong>488km of road</strong> flagged for 
                maintenance. The deterioration didn't happen overnight; it was there all along, accumulating 
                between survey visits.
              </p>
              <p className="text-rose-800 text-sm">
                The pre-2024 figures of 8-10% were not measurements of the whole network in those years — 
                they were rolling snapshots from a survey cycle that could leave a road unmeasured for over 
                half a decade.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing 
              the condition of its roads. For any Staffordshire unclassified road incident before 2024, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — and how many years had passed since the previous visit?</li>
              <li>• If 14% of U roads were RED the moment full surveys began, how long had yours been deteriorating unmeasured?</li>
              <li>• How did the council track deterioration on a road it might not survey for six years?</li>
              <li>• Can condition records from the old survey technique even be compared with the new AI-driven approach?</li>
            </ul>
            <p className="text-white font-medium">
              A council can't claim detailed knowledge of a network where individual roads went up to six 
              years between measurements — and its own report concedes the old technique under-recorded 
              the problem.
            </p>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            143,689 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Repair volumes have more than doubled since 2020/21 — and 2024/25 set a record
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled (council estimate)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Per day</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">15,502</td>
                  <td className="px-6 py-4 text-center">~42</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">30,832</td>
                  <td className="px-6 py-4 text-center">~84</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">27,610</td>
                  <td className="px-6 py-4 text-center">~76</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">32,437</td>
                  <td className="px-6 py-4 text-center">~89</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">37,308</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">~102</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">143,689</td>
                  <td className="px-6 py-4 text-center font-bold">~79</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Trend Is The Story</h3>
              <p className="text-surface-600 text-sm mb-3">
                Pothole repairs went from 15,502 in 2020/21 to 37,308 in 2024/25 — an increase of 141% in 
                four years, with another 33,600 expected in 2025/26 "depending on winter severity". A 
                network needing more repairs every year is a network deteriorating faster than it is 
                being fixed.
              </p>
              <p className="text-surface-600 text-sm">
                Defects forming at over 100 a day routinely appear between inspections — exactly the 
                scenario where prior reports and photographic evidence decide claims.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">"Rather Than Chasing Numbers"</h3>
              <p className="text-amber-800 text-sm mb-3">
                The report says the council "has adopted a 'right first time' approach, targeting larger 
                areas and carrying out higher-quality repairs wherever possible" — a shift away from 
                "simply addressing high volumes".
              </p>
              <p className="text-amber-800 text-sm">
                A sensible policy — but also an implicit acknowledgement that years of quick-fill patching 
                didn't hold. If a previous repair at your pothole's location failed, that failure history 
                is itself evidence worth obtaining.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The 181-Year Admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 181-Year Admission
          </h2>
          <p className="text-surface-600 mb-8">
            In October 2025 the council published its "Roadmap to a Better Network" — and quantified 
            its own backlog in remarkable terms
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="It comes as new figures reveal the scale of the challenge: at current funding levels, it would take 181 years to repair every road in Staffordshire, and 515 years to bring all bridges and structures up to standard."
              source="Staffordshire County Council newsroom, 'Staffordshire launches highways overhaul', 2 October 2025"
            />
            <QuoteCard 
              quote="For too long, Staffordshire's roads have been stuck in a cycle of decline, and residents are rightly frustrated. We want to stop the slide of our roads into decline and turn the tide so residents can see and feel the difference."
              source="Peter Mason, Cabinet Member for Strategic Highways, Staffordshire County Council, October 2025"
            />
            <QuoteCard 
              quote="While the overall condition of Staffordshire's road bridges and footbridges are currently rated as 'good', there is growing pressure on the asset base due to ageing infrastructure and limited capital investment. Critical load-bearing elements are now rated only as 'fair', and without timely intervention, further deterioration is expected."
              source="Staffordshire County Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What These Admissions Mean</h3>
              <p className="text-surface-600 text-sm mb-3">
                A cabinet member describing the roads as "stuck in a cycle of decline", and an official 
                figure of 181 years to clear the carriageway backlog, amount to documented, public knowledge 
                that the network is deteriorating faster than the council can repair it.
              </p>
              <p className="text-surface-600 text-sm">
                Knowledge of a network-wide backlog raises the standard for what a "reasonable" inspection 
                and repair regime looks like under Section 58 — particularly on the C and unclassified 
                roads where the council's own data shows the decline is concentrated.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions Worth Asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• If the backlog is 181 years at current funding, how was your road prioritised within it?</li>
                <li>• Was the defect that damaged your vehicle on a road already flagged RED in the council's own surveys?</li>
                <li>• Did inspection frequency increase on the C-road network as its RED percentage doubled?</li>
                <li>• What did the council do between identifying decline and the date of your incident?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Where the money goes */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Money: GREEN Spend, Shrinking Prevention
          </h2>
          <p className="text-surface-600 mb-8">
            Six years of spending data from the transparency report — capital spend has nearly doubled, 
            but the preventative share keeps falling
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">DfT capital allocation</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Capital spend</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">% preventative</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">% reactive</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">£33.8m</td>
                  <td className="px-6 py-4 text-center">£39.3m</td>
                  <td className="px-6 py-4 text-center">38%</td>
                  <td className="px-6 py-4 text-center">23%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">£25.1m</td>
                  <td className="px-6 py-4 text-center">£35.5m</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">41%</td>
                  <td className="px-6 py-4 text-center">25%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">£25.1m</td>
                  <td className="px-6 py-4 text-center">£41.4m</td>
                  <td className="px-6 py-4 text-center">37%</td>
                  <td className="px-6 py-4 text-center">23%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">£32.7m</td>
                  <td className="px-6 py-4 text-center">£55.9m</td>
                  <td className="px-6 py-4 text-center">33%</td>
                  <td className="px-6 py-4 text-center">21%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">£28.3m</td>
                  <td className="px-6 py-4 text-center">£64.7m</td>
                  <td className="px-6 py-4 text-center">33%</td>
                  <td className="px-6 py-4 text-center">22%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2025/26 (projected)</td>
                  <td className="px-6 py-4 text-center font-bold">£55.0m</td>
                  <td className="px-6 py-4 text-center font-bold">£66.4m</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">29%</td>
                  <td className="px-6 py-4 text-center font-bold">22%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Council Says "Prevention Is Better Than Cure"</h3>
              <p className="text-surface-600 text-sm">
                The report states Staffordshire follows "a 'prevention is better than cure' philosophy". 
                Yet the preventative share of spending has fallen from 41% in 2021/22 to a projected 29% 
                in 2025/26 — a decline in four consecutive reporting years, even as total capital spend 
                nearly doubled. The reactive share, by contrast, has barely moved.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">What 2025/26 Buys</h3>
              <p className="text-amber-800 text-sm">
                The council plans to treat approximately 1.4 million m² of carriageway through over 450 
                preventative schemes, deliver 13 structural schemes backed by £10.9 million, repair around 
                10,000 defects as surface-dressing preparation, and fill up to 33,600 potholes "depending 
                on winter severity" — plus £14.5 million over three years for bridges and £1.25 million 
                for drainage where "poor drainage is accelerating surface deterioration".
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against Staffordshire: An Honest Assessment
          </h2>
          <p className="text-surface-600 mb-8">
            A big-spending council with a documented decline problem — here's how that cuts both ways
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — spent £64.7m against a £28.3m DfT allocation in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 100% of A, B, C and (since 2024) unclassified roads now surveyed annually with AI-driven video
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management strategy (HIAMP), lifecycle planning and a new central asset system
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Record repair output — 37,308 potholes filled in 2024/25 shows an active reactive regime
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence, especially for incidents from 2024 onwards. 
                Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition and AMBER best practice — only Spend is GREEN
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED C roads doubled from 6% to 12% since 2020 — rising every year since 2021
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 59% of the network surveyed "up to every 6 years" before 2024 — RED jumped to 14% when full surveys began
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 143,689 potholes in five years, repairs up 141% — defects form faster than inspections catch them
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Publicly admitted 181-year road repair backlog and a network "stuck in a cycle of decline"
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Preventative spending share down from 41% to 29% in four years
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council that spends big but admits a generational backlog, your claim lives or dies 
              on the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, the council's own reporting channels) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, failed previous patching)</li>
              <li>• The road's class — on a C road, cite the doubling RED trend; on a U road before 2024, the six-year survey cycle is your strongest structural argument</li>
              <li>• The council's own RED/AMBER condition rating for your road, obtainable via its annual survey data</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and 
              cites Staffordshire's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Staffordshire?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A 181-year backlog demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No six-year survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No C-road decline analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ C-road RED doubling documented</li>
                <li>✅ Six-year U-road survey gap argued</li>
                <li>✅ 143,689 potholes in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Staffordshire</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=staffordshire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Staffordshire Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Staffordshire says it fills over 30,000 potholes a year — doesn't that prove the roads are well maintained?
              </h3>
              <p className="text-surface-600 text-sm">
                No — it proves the opposite. A network that needs 30,000-plus pothole repairs a year is a 
                network producing defects faster than planned maintenance can prevent them. Repairs hit a 
                record 37,308 in 2024/25 — over 100 every day — and the five-year total is 143,689. Section 58 
                turns on whether the specific defect that damaged your vehicle was reasonably inspected and 
                repaired, not on how many others the council patched.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on an unclassified or residential road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 3,488km — 59% of Staffordshire's road network — and before 2024 
                the council surveyed them, in its own words, "on a frequency up to every 6 years". When 100% 
                annual surveys began in 2024, the proportion in RED condition jumped from 8% to 14% — roughly 
                488km of residential streets and rural lanes flagged for maintenance that the old survey cycle 
                had not captured.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Staffordshire's DfT Spend rating is GREEN — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The council spent £64.7 million of capital in 2024/25 against a £28.3 million DfT 
                allocation, which is why Spend is GREEN. But Condition and Best Practice are both AMBER, 
                and the preventative share of spending has fallen from 41% in 2021/22 to a projected 29% 
                in 2025/26. Your claim is decided on the specific defect and the inspection record for that 
                road, not the size of the council's chequebook.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the council's "181 years" admission help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. In October 2025 Staffordshire publicly stated that at current funding levels it would 
                take 181 years to repair every road in the county, and 515 years to bring all bridges and 
                structures up to standard. That is documented knowledge of a network-wide repair backlog — 
                powerful context when testing whether the council's inspection and repair regime was 
                "reasonable" under Section 58 for the road that damaged your vehicle.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                My pothole was on a C road — does the condition data help me?
              </h3>
              <p className="text-surface-600 text-sm">
                Strongly. C roads are the worst-performing class in Staffordshire's own data: the proportion 
                in RED condition has doubled from 6% in 2020 to 12% in 2024, and only 59% of C roads are now 
                in good condition. Because the council surveys 100% of its B and C network every year, it 
                cannot argue it was unaware of the deterioration — the trend is documented in its own 
                transparency report.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                A roads jumped to 86% green in 2024 — does that weaken a claim on a main road?
              </h3>
              <p className="text-surface-600 text-sm">
                Not necessarily. The leap from 71% to 86% green in a single year coincides with step changes 
                across the 2024 survey data — the same year RED unclassified roads jumped from 8% to 14%, 
                which the council attributes to a change in survey technique. A-road claims still turn on 
                the specific defect: prior reports, photographs showing size and age, and the inspection log 
                for that stretch of road.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a 
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a 
              href="https://www.staffordshire.gov.uk/roads-parking-and-transport/highway-maintenance/managing-highway/local-highways-maintenance"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Staffordshire County Council Local Highways Maintenance Transparency Report (2025)
            </a> | <a 
              href="https://www.staffordshire.gov.uk/newsroom/2025/staffordshire-launches-highways-overhaul-fix-more-roads-and-fight-fairer-funding"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Staffordshire County Council newsroom — Roadmap to a Better Network (October 2025)
            </a>. 
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
